import type { User } from "firebase/auth";
import {
  GAME_MODE_LABEL,
  loadGameAccessMode,
  saveGameAccessMode,
  userCanAccessGmMode,
  type GameAccessMode,
} from "@/game/game-mode";
import { GameEvents } from "@/game/events/game-events";

export type GameModeControlHandle = {
  getMode: () => GameAccessMode;
  canGm: boolean;
  teardown: () => void;
};

export function mountGameModeControl(
  host: HTMLElement,
  user: User,
): GameModeControlHandle {
  const canGm = userCanAccessGmMode(user);
  let mode = loadGameAccessMode(user.uid, canGm);

  const dispatch = () => {
    window.dispatchEvent(
      new CustomEvent(GameEvents.GameModeChanged, {
        detail: { mode, userId: user.uid, canGm },
      }),
    );
  };

  const compact = host.classList.contains("game-mode-host--compact");
  const playerLabel = compact ? "เล่น" : GAME_MODE_LABEL.player;

  const render = () => {
    if (!canGm) {
      host.innerHTML = `
        <div class="game-mode-pill game-mode-pill--player" title="โหมดผู้เล่น">
          ${playerLabel}
        </div>
      `;
      host.classList.add("game-mode-control--readonly");
      return;
    }

    host.classList.remove("game-mode-control--readonly");
    host.innerHTML = `
      <div class="game-mode-switch" role="group" aria-label="โหมดเกม">
        <button type="button" class="game-mode-btn${mode === "player" ? " game-mode-btn--active" : ""}" data-mode="player" title="โหมดผู้เล่น">
          ${playerLabel}
        </button>
        <button type="button" class="game-mode-btn${mode === "gm" ? " game-mode-btn--active" : ""}" data-mode="gm" title="โหมด GM">
          ${GAME_MODE_LABEL.gm}
        </button>
      </div>
    `;
  };

  const onClick = (event: Event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-mode]");
    if (!btn || !canGm) return;
    const next = btn.dataset.mode === "gm" ? "gm" : "player";
    if (next === mode) return;
    mode = next;
    saveGameAccessMode(user.uid, mode);
    render();
    dispatch();
  };

  host.addEventListener("click", onClick);
  render();
  dispatch();

  return {
    getMode: () => mode,
    canGm,
    teardown: () => {
      host.removeEventListener("click", onClick);
      host.innerHTML = "";
    },
  };
}
